import { Component } from "@angular/core";
import { takeUntil } from "rxjs/operators";
import { SessionService } from "src/app/session.service";
import { DashboardService } from "../../dashboard.service";
import { GenericWidgetComponent } from "../generic-widget/generic-widget.component";
import { Widget } from "../widgets";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const MONTHS_FULL = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
@Component({
  selector: "app-installs-widget",
  templateUrl: "./installs-widget.component.html",
  styleUrls: ["./installs-widget.component.scss"],
})
export class InstallsWidgetComponent extends GenericWidgetComponent {
  widget!: Widget;
  installsChartOptions: any;
  installs: any;
  installData: any;
  bucket: "day" | "week" | "month" | "quarter" | "year" = "day";

  constructor(
    private DashboardService: DashboardService,
    private SessionService: SessionService,
  ) {
    super();
  }

  get accountID() {
    return this.SessionService.currentAccountValue!.accountID;
  }

  get accountAppID() {
    return this.SessionService.currentAppValue!.id;
  }

  ngOnInit(): void {}

  ngAfterViewInit() {
    super.ngAfterViewInit();
    this.initData();

    this.filterForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(() => {
        if (
          this.filterForm.valid &&
          this.filterForm.controls.startDate.value <
            this.filterForm.controls.endDate.value
        ) {
          this.initData();
        }
      });
  }

  initData() {
    this.filterForm.controls.endDate.value.setHours(23, 59, 59);

    this.elementLoader
      .waitForEvent(
        this.DashboardService.getInstalls(
          this.accountID,
          this.accountAppID,
          this.filterForm.controls.startDate.value,
          this.filterForm.controls.endDate.value,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((res) => {
        this.installData = res as any;
        this.installs = this.installData.total_installs;
        this.bucket = this.installData?.installs?.bucket || "day";

        this.initInstallsChart();
      });
  }

  initInstallsChart() {
    const type = this.bucket;

    const installDataObj: {
      type: "day" | "week" | "month" | "quarter" | "year";
      data: {
        date: Date;
        total: number;
      }[];
    } = { type, data: [] };

    for (let dateRange of this.installData.installs.installs_range) {
      installDataObj.data.push({
        date: new Date(dateRange.bucket_start),
        total: dateRange.installs,
      });
    }

    this.installsChartOptions = {
      grid: {
        left: 30,
        top: 0,
        right: 30,
        bottom: 22,
      },

      xAxis: {
        type: "category",
        boundaryGap: false,

        axisLabel: {
          color: "#fff",
          hideOverlap: false,
          formatter: (d: any) => {
            return this.getXLabel(d, type as any);
          },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
      },
      yAxis: {
        type: "value",
        splitLine: {
          show: false,
        },
        show: false,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: (params: any, ticket: any, callback: any) => {
          return this.getTooltip(params, type as any, params[0].axisValue);
        },
      },
      series: [
        {
          data: installDataObj.data.map((data: any) => {
            return [data.date, data.total];
          }),
          showSymbol: false,

          label: {
            color: "#7dc4fd",
          },
          type: "bar",
          itemStyle: { color: "white", borderRadius: [5, 5, 0, 0] },
          large: true,
          barMaxWidth: "40%",
        },
      ],
    };
  }

  private getQuarter(date: Date): number {
    return Math.floor(date.getMonth() / 3) + 1;
  }

  getXLabel(dateString: string, type: "day" | "week" | "month" | "quarter" | "year") {
    const date = new Date(dateString);
    if (type === "day") {
      return this.getDayLabel(date);
    } else if (type === "week") {
      return this.getWeekLabel(date);
    } else if (type === "month") {
      return this.getMonthLabel(date);
    } else if (type === "quarter") {
      return `Q${this.getQuarter(date)} ${date.getFullYear()}`;
    } else if (type === "year") {
      return `${date.getFullYear()}`;
    }

    return "";
  }

  getDayLabel(date: Date) {
    if (date.getDate() === 1) {
      return MONTHS[date.getMonth()];
    }

    return date.getDate().toString();
  }

  getWeekLabel(date: Date) {
    const currentDate = date.getDate();
    if (currentDate < 7) {
      return MONTHS[date.getMonth()];
    }
    return `${date.getMonth()}/${date.getDate()}`;
  }

  getMonthLabel(date: Date) {
    const currentMonth = date.getMonth();
    if (currentMonth === 0) {
      return date.getFullYear().toString();
    }
    return MONTHS[currentMonth];
  }

  getTooltip(params: any, type: "day" | "week" | "month" | "quarter" | "year", dateString: string) {
    const date = new Date(dateString);

    if (type === "day") {
      return `
      ${MONTHS[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}
      <br>
      <strong>${params[0].value[1]}</strong>
      `;
    } else if (type === "week") {
      const endDate = new Date(date);
      endDate.setDate(endDate.getDate() + 7);
      return `
      Week Of:<br>
      ${MONTHS[date.getMonth()]} ${date.getDate()} ${date.getFullYear()} - ${
        MONTHS[endDate.getMonth()]
      } ${endDate.getDate()} ${endDate.getFullYear()}
      <br>
      <strong>${params[0].value[1]}</strong>
      `;
    } else if (type === "month") {
      return `
      ${MONTHS_FULL[date.getMonth()]} ${date.getFullYear()}
      <br><strong>${params[0].value[1]}</strong>
      `;
    } else if (type === "quarter") {
      return `
      Q${this.getQuarter(date)} ${date.getFullYear()}
      <br><strong>${params[0].value[1]}</strong>
      `;
    } else if (type === "year") {
      return `
      ${date.getFullYear()}
      <br><strong>${params[0].value[1]}</strong>
      `;
    }
    return "";
  }
}
